/**
 * Het terugkeerpatroon van een ticket.
 *
 * De codes komen 1-op-1 uit de backend en worden zo opgeslagen; ze mogen niet
 * veranderen.
 */
export enum RecurrenceType {
  DAILY = 1,
  WEEKLY = 2,
  MONTHLY = 3,
  YEARLY = 4,
}

export enum RecurrenceEnd {
  NEVER = 0,
  ON_DATE = 1,
  AFTER_COUNT = 2,
}

/** Rangtelwoord voor 'de eerste maandag van de maand'. */
export enum RecurrenceOrdinal {
  FIRST = 1,
  SECOND = 2,
  THIRD = 3,
  FOURTH = 4,
  LAST = 5,
}

/** Bitmasker voor de dagen van de week; maandag is 1, zondag 64. */
export const WEEKDAY_BITS: { day: number; bit: number; key: string }[] = [
  { day: 1, bit: 1, key: "monday" },
  { day: 2, bit: 2, key: "tuesday" },
  { day: 3, bit: 4, key: "wednesday" },
  { day: 4, bit: 8, key: "thursday" },
  { day: 5, bit: 16, key: "friday" },
  { day: 6, bit: 32, key: "saturday" },
  { day: 7, bit: 64, key: "sunday" },
];

export function bitForWeekday(isoDay: number): number {
  return isoDay < 1 || isoDay > 7 ? 0 : 1 << (isoDay - 1);
}

export class RecurrencePattern {
  id: number | null;
  type: RecurrenceType;
  interval: number;
  everyWeekday: boolean;
  weekdays: number;
  monthDay: number;
  monthOrdinal: number;
  monthWeekday: number;
  yearMonth: number;
  startDate: string | null;
  endType: RecurrenceEnd;
  endDate: string | null;
  endCount: number;
  active: boolean;
  created?: string | null;
  modified?: string | null;
  cancelledAt?: string | null;
  createdBy?: number;

  constructor(
    id: number | null = null,
    type: RecurrenceType = RecurrenceType.WEEKLY,
    interval = 1,
    everyWeekday = false,
    weekdays = 0,
    monthDay = 0,
    monthOrdinal = 0,
    monthWeekday = 0,
    yearMonth = 0,
    startDate: string | null = null,
    endType: RecurrenceEnd = RecurrenceEnd.NEVER,
    endDate: string | null = null,
    endCount = 0,
    active = true
  ) {
    this.id = id;
    this.type = type;
    this.interval = interval;
    this.everyWeekday = everyWeekday;
    this.weekdays = weekdays;
    this.monthDay = monthDay;
    this.monthOrdinal = monthOrdinal;
    this.monthWeekday = monthWeekday;
    this.yearMonth = yearMonth;
    this.startDate = startDate;
    this.endType = endType;
    this.endDate = endDate;
    this.endCount = endCount;
    this.active = active;
  }

  /**
   * An empty pattern: weekly, from today, with no end date.
   *
   * Weekly on today's weekday, not weekly on nothing. The type is already
   * weekly here without the user having chosen it, so selectType never runs and
   * would never fill it in: a brand new ticket would show four types, a start
   * date, and seven unlit days that have to be guessed at.
   */
  public static get EMPTY(): RecurrencePattern {
    const today = new Date();
    const pattern = new RecurrencePattern();

    pattern.startDate = RecurrencePattern.asIsoDate(today);
    pattern.weekdays = bitForWeekday(today.getDay() === 0 ? 7 : today.getDay());
    return pattern;
  }

  /** Maakt een echt patroon van wat de API teruggeeft. */
  public static from(raw: any): RecurrencePattern {
    const pattern = new RecurrencePattern();
    if (!raw) {
      return pattern;
    }
    pattern.id = raw.id ?? null;
    pattern.type = raw.type ?? RecurrenceType.WEEKLY;
    pattern.interval = raw.interval ?? 1;
    pattern.everyWeekday = !!raw.everyWeekday;
    pattern.weekdays = raw.weekdays ?? 0;
    pattern.monthDay = raw.monthDay ?? 0;
    pattern.monthOrdinal = raw.monthOrdinal ?? 0;
    pattern.monthWeekday = raw.monthWeekday ?? 0;
    pattern.yearMonth = raw.yearMonth ?? 0;
    pattern.startDate = raw.startDate ?? null;
    pattern.endType = raw.endType ?? RecurrenceEnd.NEVER;
    pattern.endDate = raw.endDate ?? null;
    pattern.endCount = raw.endCount ?? 0;
    pattern.active = raw.active !== false;
    pattern.created = raw.created ?? null;
    pattern.modified = raw.modified ?? null;
    pattern.cancelledAt = raw.cancelledAt ?? null;
    pattern.createdBy = raw.createdBy ?? 0;
    return pattern;
  }

  public static asIsoDate(date: Date | null): string | null {
    if (!date) {
      return null;
    }
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  }

  public static asDate(iso: string | null | undefined): Date | null {
    if (!iso) {
      return null;
    }
    const parts = iso.split("-").map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => isNaN(part))) {
      return null;
    }
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  hasWeekday(isoDay: number): boolean {
    return (this.weekdays & bitForWeekday(isoDay)) !== 0;
  }

  toggleWeekday(isoDay: number, on: boolean): void {
    const bit = bitForWeekday(isoDay);
    this.weekdays = on ? this.weekdays | bit : this.weekdays & ~bit;
  }

  get hasAnyWeekday(): boolean {
    return (this.weekdays & 0b1111111) !== 0;
  }

  /** Of de maand- of jaarregel is uitgedrukt als 'de derde dinsdag'. */
  get byOrdinalWeekday(): boolean {
    return this.monthOrdinal > 0 && this.monthWeekday > 0;
  }

  /**
   * Spiegelt de controle in de backend, zodat de gebruiker niet hoeft te
   * wachten op een foutmelding van de server.
   */
  get valid(): boolean {
    if (!this.startDate) {
      return false;
    }
    const intervalMatters =
      this.type !== RecurrenceType.DAILY || !this.everyWeekday;
    if (intervalMatters && this.interval < 1) {
      return false;
    }
    if (this.type === RecurrenceType.WEEKLY && !this.hasAnyWeekday) {
      return false;
    }
    if (
      this.type === RecurrenceType.MONTHLY ||
      this.type === RecurrenceType.YEARLY
    ) {
      if (!this.byOrdinalWeekday && this.monthDay < 1) {
        return false;
      }
      if (this.byOrdinalWeekday && this.monthDay > 0) {
        return false;
      }
    }
    if (this.type === RecurrenceType.YEARLY) {
      if (this.yearMonth < 1 || this.yearMonth > 12) {
        return false;
      }
    }
    if (this.endType === RecurrenceEnd.ON_DATE) {
      if (!this.endDate) {
        return false;
      }
      if (this.endDate < this.startDate) {
        return false;
      }
    }
    if (this.endType === RecurrenceEnd.AFTER_COUNT && this.endCount < 1) {
      return false;
    }
    return true;
  }
}

/**
 * Wat de ticketschermen over het terugkeerpatroon tonen: het patroon zelf,
 * waar dit ticket in de reeks staat, en wanneer de volgende volgt.
 */
export class RecurrenceView {
  pattern: RecurrencePattern;
  sequence: number;
  /** -1 wanneer het patroon geen einddatum heeft. */
  total: number;
  scheduledOn: string | null;
  next: string | null;
  description: string;

  constructor(
    pattern: RecurrencePattern,
    sequence = 0,
    total = 0,
    scheduledOn: string | null = null,
    next: string | null = null,
    description = ""
  ) {
    this.pattern = pattern;
    this.sequence = sequence;
    this.total = total;
    this.scheduledOn = scheduledOn;
    this.next = next;
    this.description = description;
  }

  public static from(raw: any): RecurrenceView | null {
    if (!raw) {
      return null;
    }
    return new RecurrenceView(
      RecurrencePattern.from(raw.pattern),
      raw.sequence ?? 0,
      raw.total ?? 0,
      raw.scheduledOn ?? null,
      raw.next ?? null,
      raw.description ?? ""
    );
  }

  get active(): boolean {
    return !!this.pattern && this.pattern.active;
  }

  get unbounded(): boolean {
    return this.total < 0;
  }
}

/** Een toekomstig ticket dat door annuleren of wijzigen geraakt wordt. */
export class RecurrenceAffected {
  constructor(
    public ticketId: number,
    public title: string,
    public sequence: number,
    public scheduledOn: string | null,
    public hasActivity: boolean,
    /** Of de voorgestelde regel deze datum nog plant. Bij annuleren altijd waar. */
    public fits: boolean = true
  ) {}
}

export class RecurrenceImpact {
  constructor(
    public patternId: number,
    public future: RecurrenceAffected[] = [],
    /**
     * De aflevering waarna het genereren weer oppakt als de gebruiker laat
     * staan wat al is opgepakt. De backend rekent dat uit, want het volgt uit
     * dezelfde regel als de generator zelf gebruikt.
     */
    public resumeAfterSequence: number = 0,
    public resumeAfterDate: string | null = null
  ) {}

  public static from(raw: any): RecurrenceImpact {
    if (!raw) {
      return new RecurrenceImpact(0);
    }
    const future = (raw.future ?? []).map(
      (one: any) =>
        new RecurrenceAffected(
          one.ticketId,
          one.title,
          one.sequence,
          one.scheduledOn ?? null,
          !!one.hasActivity,
          one.fits === undefined ? true : !!one.fits
        )
    );
    return new RecurrenceImpact(
      raw.patternId ?? 0,
      future,
      raw.resumeAfterSequence ?? 0,
      raw.resumeAfterDate ?? null
    );
  }

  get anyTouched(): boolean {
    return this.future.some((one) => one.hasActivity);
  }

  /**
   * Whether the proposed rule would cost anything that cannot be got back.
   *
   * Two things have to come together. A ticket has to fall outside the new
   * rule, otherwise nothing moves at all; and it has to have been worked on,
   * because an untouched ticket is replanned without anyone missing it. Only
   * then is there a decision that belongs to the user.
   *
   * Activity on its own is not enough, and asking on activity alone is how an
   * unchanged rule ended up putting the question on screen: a comment on one
   * of the tickets ahead was all it took.
   */
  get atRisk(): boolean {
    return this.future.some((one) => !one.fits && one.hasActivity);
  }

  get empty(): boolean {
    return this.future.length === 0;
  }
}

/** Wat er met de toekomstige tickets moet gebeuren bij annuleren. */
export type RecurrenceFuture = "keep" | "delete";

/**
 * Wat er met de toekomstige tickets moet gebeuren als de regel wijzigt.
 *
 * AFTER_WORKED verzet wat verzet kan worden en laat staan waaraan al gewerkt
 * is; de twee REPLAN-keuzes ruimen de hele toekomst op en plannen opnieuw
 * vanaf vandaag, de een door te verwijderen, de ander door de tickets als
 * losse tickets te laten staan.
 */
export type RecurrenceChange = "AFTER_WORKED" | "REPLAN_DELETE" | "REPLAN_KEEP";
